import { ref, update } from "firebase/database";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { type EditableScene, addZone, removeZone, renameZone } from "../lib/scene-edits.js";

interface Props {
  scenePath: string;
  scene: EditableScene;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function SceneZonesEditor({ scenePath, scene }: Props): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    const id = slugify(name) || `z-${Date.now().toString(36)}`;
    const { db } = getFirebase();
    await update(ref(db), addZone(scenePath, scene, { id, name }));
    setDraft("");
  }

  async function handleRename(id: string, name: string): Promise<void> {
    if (!name.trim()) return;
    const { db } = getFirebase();
    await update(ref(db), renameZone(scenePath, scene, id, name.trim()));
  }

  async function handleRemove(id: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-zone"))) return;
    const { db } = getFirebase();
    await update(ref(db), removeZone(scenePath, scene, id));
  }

  return (
    <section>
      <h3 className="text-sm font-semibold uppercase text-muted-foreground">
        {t("edit.scene.zones")}
      </h3>
      <ul className="mt-2 space-y-1">
        {(scene.zones ?? []).map((z) => (
          <li key={z.id} className="flex items-center gap-2 text-sm">
            <input
              className="flex-1 rounded border px-2 py-1"
              defaultValue={z.name}
              onBlur={(e) => handleRename(z.id, e.target.value)}
              aria-label={`${t("edit.scene.zone-name")} ${z.id}`}
            />
            <button
              type="button"
              className="rounded border border-rose-400 px-2 py-1 text-xs text-rose-700"
              onClick={() => handleRemove(z.id)}
            >
              {t("edit.delete")}
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={handleAdd} className="mt-2 flex gap-2 text-sm">
        <input
          className="flex-1 rounded border px-2 py-1"
          placeholder={t("edit.scene.new-zone-placeholder")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={t("edit.scene.new-zone")}
        />
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.scene.add-zone")}
        </button>
      </form>
    </section>
  );
}
